import { Injectable } from '@angular/core';
import { map, Observable, of } from 'rxjs';
import {
  AsappServiceUrls,
  ApplicationUrls,
  ConfigurationManagementUrls,
  IdentityVerificationUrl,
  MemberUrls,
} from '@core/constants';
import { ApiClient } from '@core/services/api-client.http.service';
import { ApplicationsRequestDto } from '@shared/models/requests/applications.request.dto';
import {
  ApplicationsDto,
  BranchDto,
  PaginatedResponseDto,
  ProductDto,
  UserDto,
} from '@core/services/dto';
import { ApplicationStatusCountDto } from '@core/services/dto/application-status-count.dto';
import { ApplicationStatusesRequestDto } from '@shared/models/requests/application-statuses.request.dto';
import { SiteDetailDto } from '@core/services/dto/site-detail.dto';
import { UserBranchDto } from '../dto/user-Branch.dto';
import { ApplicationGenralrequest } from '../dto/applicationGenralrequest.dto';
import { ApplicantDetailModel } from '@shared/models/applicant-details.model';
import { Applicant } from '../dto/applicant.dto';
import { ApiResponse, ApplicationApiResponse } from '../dto/api-response.dto';
import { CreditDetailModel } from '@shared/models/creditDetail.model';
import { CreditDto } from '../dto/credit.dto';
import { ApplicantComplianceModel } from '@shared/models/applicant-compliance.model';
import { UpdateApplicantRequest } from '../dto/updateapplicantrequest';
import { UpdateEmployerPayload } from '../dto/update-applicant-employer.dto';
import { ApplicantOtherIncomesRequest } from '../dto/update-applicant-income.dto';
import { AddOrUpdateApplicantCompliancesRequest } from '@shared/models/requests/update-applicant-compliance.request.dto';
import { AddIdentificationDetails } from '@shared/models/requests/add-identification-details-request.dto';
import { Country, Province } from '../dto/countryList.dto';
import { Language, SiteConfigDto } from '../dto/site-configuration.dto';
import { EquifaxConfigurations } from '../dto/EquifaxConfiguration.dto';
import {
  ApplicationResumeByStaff,
  NewApplicationStartByStaff,
} from '@shared/models/requests/ApplicationResumeByStaff.request.dto';
import { FlinksApplicantModelResponse } from '../dto/flinks-model.dto';
import { FlinksTransactionsResponse } from '../dto/flinks-transaction.dto';

export interface LanguageItem {
  id: number;
  label: string;
  iconSrc: string;
  code: string;
  action: string;
}
@Injectable({
  providedIn: 'root',
})
export class ApplicationsApiService {
  constructor(private api: ApiClient) { }

  getApplications(
    request: ApplicationsRequestDto
  ): Observable<PaginatedResponseDto<ApplicationsDto>> {
    return this.api.get<PaginatedResponseDto<ApplicationsDto>>(
      ApplicationUrls.applications,
      request
    );
  }

  getStatusesCount(
    request: ApplicationStatusesRequestDto
  ): Observable<ApiResponse<ApplicationStatusCountDto[]>> {
    return this.api.get<ApiResponse<ApplicationStatusCountDto[]>>(
      ApplicationUrls.count,
      request
    );
  }
  getSiteDetails(): Observable<SiteDetailDto> {
    return this.api.get<SiteDetailDto>(AsappServiceUrls.siteDetails);
  }

  getUserBranch(): Observable<UserBranchDto[]> {
    return this.api.get<UserBranchDto[]>(AsappServiceUrls.userBranch);
  }

  getBranches(): Observable<{ result: BranchDto[] }> {
    return this.api.get(ApplicationUrls.branches);
  }
  getStaffUsers(): Observable<{ result: UserDto[] }> {
    return this.api.get(ApplicationUrls.staffUsers);
  }

  getProducts(
    productType: number
  ): Observable<PaginatedResponseDto<ProductDto>> {
    return this.api.get<PaginatedResponseDto<ProductDto>>(
      ApplicationUrls.products,
      { productType }
    );
  }

  getProductById(
    productId: number
  ): Observable<PaginatedResponseDto<ProductDto>> {
    return this.api.get(ApplicationUrls.productById(productId));
  }

  getProductSummariesByIds(productIds: number[]): Observable<PaginatedResponseDto<ProductDto>> {
    return this.api.get(ApplicationUrls.getProductSummariesByIds(productIds));
  }

  updateGeneralDetails(data: ApplicationGenralrequest): Observable<any> {
    return this.api.put(`${ApplicationUrls.updateGeneralDetails}`, data);
  }
  getApplicationConfigurationBySiteId(siteId?: string): Observable<any> {
    const url = siteId
      ? `${ConfigurationManagementUrls.getApplicationConfigurationBySiteId}?siteId=${encodeURIComponent(siteId)}`
      : ConfigurationManagementUrls.getApplicationConfigurationBySiteId;

    return this.api.get<any>(url);
  }

  getProductSnapDetails(applicationId: number): Observable<any> {
    return this.api.get<any>(
      ApplicationUrls.applicationProductSnapAPI(applicationId)
    );
  }
  getApplicantById(applicantId: number): Observable<ApiResponse<Applicant>> {
    return this.api.get<ApiResponse<Applicant>>(
      ApplicationUrls.ApplicantById(applicantId)
    );
  }

  exportApplication(request: ApplicationsRequestDto): Observable<any> {
    return this.api.get(ApplicationUrls.exportApplications, request);
  }

  getExportStatus(jobId: string): Observable<{ jobId: string; status: string }> {
    return this.api.get<{ jobId: string; status: string }>(
      `${ApplicationUrls.exportStatus}/${jobId}`
    );
  }

  downloadExport(jobId: string): Observable<Blob> {
    return this.api.getFile(
      `${ApplicationUrls.downloadExport}/${jobId}`
    );
  }

  downloadApplicationZip(applicationId: number, defaultCountry: string): Observable<Blob> {
    return this.api.getFile(
      ApplicationUrls.downloadApplicationZip(applicationId, defaultCountry)
    );
  }

  printApplication(applicationId: number, defaultCountry: string): Observable<Blob> {
    return this.api.getFile(ApplicationUrls.printApplication(applicationId, defaultCountry));
  }

  getLCSApplicationSummary(applicationId: number): Observable<any> {
    return this.api.post<any>(ApplicationUrls.LCSApplicationSummary, {
      applicationId,
      requestId: 'cf57361c-ea77-4b46-a47c-69c55ba215a4',
    });
  }

  getCreditReport(
    applicationId: number,
    applicantId: number
  ): Observable<ApplicationApiResponse<CreditDto>> {
    return this.api.get<ApplicationApiResponse<CreditDetailModel>>(
      ApplicationUrls.CreditReport(applicationId, applicantId)
    );
  }
  getAllApplicantComplianceById(
    applicantId: number
  ): Observable<ApiResponse<ApplicantComplianceModel[]>> {
    return this.api.get<ApiResponse<ApplicantComplianceModel[]>>(
      ApplicationUrls.applicantCompliancesById(applicantId)
    );
  }
  updateApplicant(
    updateApplicantRequest: UpdateApplicantRequest
  ): Observable<any> {
    const payload: any = { ...updateApplicantRequest, IsRequestTeamPortal: true };
    return this.api.put(
      `${ApplicationUrls.updateApplicant}`,
      payload
    );
  }
  updateApplicantEmployeement(
    updateApplicantEmpRequest: UpdateEmployerPayload
  ): Observable<any> {
    const payload: any = { ...updateApplicantEmpRequest, IsRequestTeamPortal: true };
    return this.api.put(
      `${ApplicationUrls.updateEmployer}`,
      payload
    );
  }
  updateApplicantIncome(
    updateApplicantIncomeRequest: ApplicantOtherIncomesRequest
  ): Observable<any> {
    return this.api.post(
      `${ApplicationUrls.updateApplicantIncome}`,
      updateApplicantIncomeRequest
    );
  }
  updateApplicantCompliance(
    updateComplianceRequest: AddOrUpdateApplicantCompliancesRequest
  ): Observable<any> {
    const payload: any = { ...updateComplianceRequest, IsRequestTeamPortal: true };
    return this.api.post(
      `${ApplicationUrls.updateApplicantCompliance}`,
      payload
    );
  }
  addApplicantIdentificationDetails(
    request: AddIdentificationDetails
  ): Observable<any> {
    return this.api.put(`${ApplicationUrls.addIdentificationDetails}`, request);
  }
  getTelcoAndSignzyLogDetails(request: {
    applicantId: number;
    siteId: string;
    all: boolean;
    verificationType: string;
    apiName: string;
  }): Observable<any> {
    return this.api.post(
      IdentityVerificationUrl.IdentityFetchTelcoSignzy,
      request
    );
  }
  getCountries(): Observable<ApiResponse<Country[]>> {
    return this.api.get<ApiResponse<Country[]>>(
      ApplicationUrls.getCountryList('Eng')
    );
  }

  getProvinces(CountryId: number): Observable<ApiResponse<Province[]>> {
    return this.api.get<ApiResponse<Province[]>>(
      ApplicationUrls.getProvinceList(CountryId)
    );
  }
  getLanguageItems(
    siteId: string
  ): Observable<{ languages: LanguageItem[]; sitePreferredLanguage: string }> {
    return this.api
      .get<SiteConfigDto>(
        ConfigurationManagementUrls.getLanguageDatabySiteId(siteId)
      )
      .pipe(
        map((response: SiteConfigDto) => {
          const preferred = response.preferredLanguage;
          const languages = response.languages.map((item: Language) => ({
            id: item.id,
            label: item.description,
            iconSrc: `https://bankiodev.blob.core.windows.net/themes/82a2dd81-9bdc-422c-be50-000000000037/flags/${item.languageCode}.png`,
            code: item.languageCode,
            action: item.languageCode,
          }));
          return { languages, sitePreferredLanguage: preferred };
        })
      );
  }
  getEquifaxConfiguration(): Observable<boolean> {
    return this.api.get<boolean>(
      ConfigurationManagementUrls.getEquifaxConfiguration
    );
  }
  addApplicationLog(payload: any) {
    return this.api.post(ApplicationUrls.AddApplicationLog, payload);
  }
  applicationResumeByStaff(request: ApplicationResumeByStaff): Observable<any> {
    return this.api.post(ApplicationUrls.ApplicationResumeByStaff, request);
  }
  newApplicationStartByStaff(
    request: NewApplicationStartByStaff
  ): Observable<any> {
    return this.api.post(ApplicationUrls.NewApplicationStartByStaff, request);
  }
  getApplicationLinkedMemberLoginInfo(applicationId: number): Observable<any> {
    return this.api.get<any>(MemberUrls.getApplicationLinkedMemberInfo(applicationId));
  }
  getKountDetails(request: {
    applicantId: number;

  }): Observable<any> {

    return this.api.get(IdentityVerificationUrl.IdentityFetchKount, request);

  }
  runKountDetails(request: any): Observable<any> {

    return this.api.post(IdentityVerificationUrl.RunKount, request);

  }
  updateKountDetails(logId: number, isMarkedAsReview: boolean): Observable<any> {
    return this.api.put<SiteConfigDto>(IdentityVerificationUrl.IdentityUpdateKount(logId, isMarkedAsReview));
  }

  deleteKountEntry(applicantId: string): Observable<any> {
    return this.api.delete<SiteConfigDto>(IdentityVerificationUrl.IdentityDeleteKount(applicantId));
    // return this.http.delete(`/api/ServiceLogs/${applicantId}`);
  }

  getFlinksTransactions(applicantId: string): Observable<FlinksTransactionsResponse> {
    return this.api.get<FlinksTransactionsResponse>(IdentityVerificationUrl.IdentityFetchFlinksTransactions(applicantId));
  }

  getFlinksModel(applicantId: string): Observable<FlinksApplicantModelResponse> {
    return this.api.get<FlinksApplicantModelResponse>(IdentityVerificationUrl.IdentityFetchFlinksModel(applicantId));
  }
}
